import {
  SkyCheckbox,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { ConnectorSummary } from "@shared/types/connectors";
import type { DataflowSummary } from "@shared/types/dataflows";
import type { StartRunBody } from "@shared/types/runs";
import type { ScheduleSummary } from "@shared/types/schedules";
import { incrementalCapable } from "@/components/schedules/scheduleForm";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { useConnectors } from "@/hooks/use-connectors";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useDataflowEntities } from "@/hooks/use-dataflows";
import { useStartRun } from "@/hooks/use-runs";
import { useTableRunsEnabled } from "@/hooks/use-server-build";
import { newIdempotencyKey } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { absolutePath } from "@/lib/routes";
import {
  AFTER_FIRST_IMPORT_HINT,
  AFTER_FIRST_IMPORT_HINT_MIXED,
  AFTER_FIRST_IMPORT_HINT_SOME,
  AFTER_FIRST_IMPORT_HINT_WHOLE_DAYS,
  FIRST_IMPORT_HINT,
  MOVED_MIXED_IMPORT_HINT,
  MOVED_MIXED_IMPORT_HINT_WHOLE_DAYS,
  FULL_ONLY_IMPORT_NOTE,
  UNTICKED_CONNECTIONS_NOTE,
  UNTICKED_CONNECTIONS_NOTE_AFTER_IMPORT,
  UNTICKED_CONNECTIONS_NOTE_AFTER_MOVE,
  UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS,
  UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_MOVE,
  UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_IMPORT,
  readsWholeDays,
  everyRowSentence,
  firstImportNote,
  hasImported,
  importHintFor,
  isMovedImportHint,
} from "@/lib/runs/refreshHint";
import {
  leftOutNextStep,
  leftOutSentence,
  runnableSentence,
  scheduleLeftOut,
} from "@/lib/runs/leftOut";
import { SCHEDULE_RUN_RULE } from "@/lib/schedules/runRule";
import {
  TABLE_LIST_ERROR,
  cutListNote,
  searchPending,
  useServerTableSearch,
} from "./serverTableSearch";
import { TablePicker } from "./TablePicker";
import { localDateIso, sinceHelp } from "./tableRuns";

/**
 * Start a run on demand (FR-52), ported from Studio's `V2RunDialog`.
 *
 * The donor's three-way "resume / rerun / full refresh" choice does not apply
 * to a fresh run — it belongs to the retry path, where it lives here too — so
 * what is kept is the shape that made that dialog good: every option states its
 * cost before it is chosen, and the failure is shown inline with the reference
 * id rather than as a toast that vanishes while the user is reading it.
 *
 * A run is a schedule OR a single connection, never both and never neither,
 * which is the same rule `startRunBodySchema` enforces. The radio group makes
 * that structural rather than a validation message.
 *
 * Session 10 (P7): "Full refresh" is offered only when the target imports
 * incrementally — the connection itself, or any connection of the schedule —
 * because on a full connection every import already reads every row.
 *
 * Round 4, item 7: a schedule with two or more incremental connections gets a
 * box per connection ("Connections to import in full"), pre-ticked from the
 * schedule's own set, and "Full refresh" ticks them all. The start sends
 * `fullRefreshDataflowIds` for some, `fullRefresh: true` for all, neither for none.
 *
 * Product gaps row 2: for ONE connection, "Choose tables" narrows the run to
 * some of its selected tables (`tables` on the body), each read from its own
 * watermark; with Full refresh they are re-synced whole (`mode: "full"`), and
 * "Read changes since" reads each from a date (`mode: "since"`; only on an incremental
 * connection, as Full refresh, since a full one reads every table whole). A schedule run
 * is never scoped. Shown only where the deployment offers table runs.
 *
 * #1077: "imports incrementally" is the connection's refresh type AND its connector's
 * `isIncrementalSupported` (`incrementalCapable`, the schedule row's rule). A full-only connector's
 * connection can store `incremental`, yet every import reads every row, so it is offered no Full
 * refresh and the dialog says the schedule row's sentence instead (`FULL_ONLY_IMPORT_NOTE`).
 *
 * Round 6, S5: the Runs page no longer opens this dialog. A connection's "Import with options"
 * (its editor header and its row menu on Connections) opens it with `targetFixed`: the record is
 * already chosen, so the target is named as text and there is no kind or target to pick. The
 * Schedules list's Run now still opens it on its schedule, with the picker.
 */

/** The most selected tables the picker lists. */
const TABLE_PICKER_PAGE = 200;

/** The help text under the checkbox (contract §8). */
export const FULL_REFRESH_HELP =
  "Imports every row again instead of continuing from the last import. Takes longer.";

/** #1583: "All selected tables" on a connection that imports only changes. */
export const ALL_TABLES_INCREMENTAL =
  "Imports every selected table, each from its own last successful import.";

/**
 * #1583: "All selected tables" on a connection that reads every row each time (a full refresh
 * connection, or a connector that cannot import incrementally), so no continuation is promised.
 */
export const ALL_TABLES_FULL = "Imports every selected table in full.";

/** The connector catalog by name, as `incrementalCapable` reads it. */
export type ConnectorCatalog = ReadonlyMap<
  string,
  Pick<ConnectorSummary, "isIncrementalSupported" | "readsWholeDays">
>;

const NO_CATALOG: ConnectorCatalog = new Map();

/**
 * #1077: whether a connection's import reads only changes: `incremental`, on a connector that can.
 * While the catalog is loading, has failed, or does not list the connector (deactivated), the
 * summary's `refreshType` alone decides, which the server reports as `full` for a connector that
 * cannot import incrementally (`effectiveRefreshType` in DataflowService), so no state of the
 * catalog offers such a connection Full refresh or says it reads only changes.
 */
export function importsIncrementally(
  connection: Pick<DataflowSummary, "connectorName" | "refreshType">,
  connectors: ConnectorCatalog = NO_CATALOG,
): boolean {
  return connection.refreshType === "incremental" && incrementalCapable(connection, connectors);
}

/**
 * #1077: the connection's connector is listed and cannot import incrementally, so every import
 * reads every row whatever the connection stores.
 */
export function connectorIsFullOnly(
  connection: Pick<DataflowSummary, "connectorName"> | undefined,
  connectors: ConnectorCatalog,
): boolean {
  if (!connection) return false;
  return connectors.get(connection.connectorName)?.isIncrementalSupported === false;
}

/**
 * Whether the chosen target has an incremental connection, so a full refresh would change what
 * the import reads. A schedule's connections are looked up in `connections`; one not listed there
 * (not visible to this screen) is not counted.
 */
export function targetIsIncremental(
  kind: RunTargetKind,
  targetId: string,
  connections: readonly DataflowSummary[],
  schedules: readonly ScheduleSummary[],
  connectors: ConnectorCatalog = NO_CATALOG,
): boolean {
  if (!targetId) return false;
  const incremental = new Set(
    connections
      .filter((connection) => importsIncrementally(connection, connectors))
      .map((connection) => connection.id),
  );
  if (kind === "dataflow") return incremental.has(targetId);
  const schedule = schedules.find((row) => row.id === targetId);
  return runnableMembersOf(schedule, connections).some((id) => incremental.has(id));
}

/**
 * #1673: the schedule's connections a Run now imports, in its order: a member it leaves out
 * (disabled, target changed, Draft or deleted, `scheduleLeftOut`) is never offered a full refresh.
 */
function runnableMembersOf(
  schedule: Pick<ScheduleSummary, "dataflowIds"> | undefined,
  connections: readonly DataflowSummary[],
): string[] {
  return scheduleLeftOut(schedule?.dataflowIds ?? [], connections).runnableIds;
}

export type RunTargetKind = "schedule" | "dataflow";

/**
 * Whether a schedule imports some connection in full on its own runs (P9; round 4, item 7): its
 * mode is full, or its set names a connection. A connection starts unticked.
 */
export function prefersFullRefresh(
  kind: RunTargetKind,
  targetId: string,
  schedules: readonly ScheduleSummary[],
): boolean {
  if (kind !== "schedule" || !targetId) return false;
  const schedule = schedules.find((row) => row.id === targetId);
  return schedule?.refreshMode === "full" || (schedule?.fullRefreshDataflowIds ?? []).length > 0;
}

/** The target's incremental connections this screen can see, in the target's own order. */
function incrementalConnectionsOf(
  kind: RunTargetKind,
  targetId: string,
  connections: readonly DataflowSummary[],
  schedules: readonly ScheduleSummary[],
  connectors: ConnectorCatalog,
): string[] {
  if (!targetId) return [];
  const incremental = new Set(
    connections.filter((row) => importsIncrementally(row, connectors)).map((row) => row.id),
  );
  const ids =
    kind === "dataflow"
      ? [targetId]
      : runnableMembersOf(
          schedules.find((row) => row.id === targetId),
          connections,
        );
  return ids.filter((id) => incremental.has(id));
}

/**
 * Round 4, item 7: the boxes a schedule's Run now starts with: the incremental connections its
 * own runs import in full (its set, or every one for an older server's full mode). A run by
 * hand is run the way the schedule runs by itself, unless the user changes it.
 */
export function initialFullRefreshSet(
  targetId: string,
  connections: readonly DataflowSummary[],
  schedules: readonly ScheduleSummary[],
  connectors: ConnectorCatalog = NO_CATALOG,
): string[] {
  if (!prefersFullRefresh("schedule", targetId, schedules)) return [];
  const schedule = schedules.find((row) => row.id === targetId);
  const stored = new Set(
    schedule?.fullRefreshDataflowIds ??
      (schedule?.refreshMode === "full" ? schedule.dataflowIds : []),
  );
  return incrementalConnectionsOf("schedule", targetId, connections, schedules, connectors).filter(
    (id) => stored.has(id),
  );
}

export interface RunNowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connections: DataflowSummary[];
  schedules: ScheduleSummary[];
  /** Pre-select one target, e.g. the row whose menu opened this dialog. */
  initialTarget?: { kind: RunTargetKind; id: string };
  /** Whether the two kinds may be chosen; a schedules screen fixes the kind. */
  allowKindChoice?: boolean;
  /**
   * Round 6, S5: the target is `initialTarget` and cannot change (opened from that record): it is
   * shown as text, with no kind choice and no picker.
   */
  targetFixed?: boolean;
}

/** The body of POST /runs for the dialog's choices. */
function startRunBody({
  kind,
  targetId,
  offerFullRefresh,
  fullRefresh,
  forcedHere,
  tables,
}: {
  kind: RunTargetKind;
  targetId: string;
  offerFullRefresh: boolean;
  fullRefresh: boolean;
  /** The schedule's incremental connections ticked for a full import. */
  forcedHere: string[];
  /**
   * The chosen tables and the optional since date, or null for every selected table. `zone` is
   * the reader's display zone, whose midnight starts the date (#1078).
   */
  tables: { since: string; entityNames: string[]; zone: string } | null;
}): StartRunBody {
  // Sent only while the option is shown: a box ticked for a previous target never forces
  // a full run of one that was not offered it.
  const forceFull = offerFullRefresh && fullRefresh;
  const forced = forceFull ? { fullRefresh: true } : {};
  if (kind === "schedule") {
    // Item 7: some of a schedule's incremental connections.
    const some =
      !fullRefresh && forcedHere.length > 0 ? { fullRefreshDataflowIds: forcedHere } : {};
    return { scheduleId: targetId, ...forced, ...some };
  }
  if (!tables) return { dataflowId: targetId, ...forced };
  // Chosen tables carry their own mode: whole (Full refresh), from a date, or from each
  // table's own watermark. `fullRefresh` never travels with them.
  return {
    dataflowId: targetId,
    tables: {
      mode: tablesMode(tables.since, forceFull),
      ...(tables.since ? { since: tables.since, sinceTimeZone: tables.zone } : {}),
      entityNames: tables.entityNames,
    },
  };
}

function tablesMode(since: string, fullRefresh: boolean): "since" | "full" | "incremental" {
  if (since) return "since";
  return fullRefresh ? "full" : "incremental";
}

/** The picker's choices: every schedule (a disabled one says it can still run), or Ready connections. */
function targetOptions(
  kind: RunTargetKind,
  schedules: readonly ScheduleSummary[],
  readyConnections: readonly DataflowSummary[],
): Array<{ value: string; text: string }> {
  if (kind === "schedule") {
    return schedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.enabled
        ? schedule.name
        : `${schedule.name} (disabled, but it can still be run by hand)`,
    }));
  }
  return readyConnections.map((connection) => ({
    value: connection.id,
    text: connection.displayName,
  }));
}

/** S3: the first-import note, only for one connection importing incrementally. */
function firstImportNoteFor(
  kind: RunTargetKind,
  targetId: string,
  connections: readonly DataflowSummary[],
  connectors: ConnectorCatalog,
): string | null {
  if (kind !== "dataflow") return null;
  const targetConnection = connections.find((row) => row.id === targetId);
  return targetConnection
    ? firstImportNote(targetConnection, readsWholeDays(targetConnection, connectors))
    : null;
}

/** #1008: a one-connection run carries no schedule id, so the schedule does not count it. */
export const NOT_COUNTED_IN_SCHEDULE =
  "A run of one connection is not counted in the schedule's Run history or success rate.";

function targetPlaceholder(kind: RunTargetKind, optionCount: number): string {
  if (optionCount > 0) return "Choose one";
  return kind === "schedule" ? "No schedule in this instance yet" : "No connection is Ready yet";
}

/**
 * #1673: a schedule's connections that this Run now leaves out, read before the click: none can
 * run (the start would be refused, so Start is off, and the next step follows each reason), or
 * some cannot (named, and the one that runs named, or how many run).
 */
export function scheduleLeftOutNotice(
  schedule: Pick<ScheduleSummary, "name" | "dataflowIds"> | undefined,
  connections: readonly DataflowSummary[],
): { blocked: boolean; title: string; message: string } | null {
  if (!schedule || schedule.dataflowIds.length === 0) return null;
  const { leftOut, runnableIds } = scheduleLeftOut(schedule.dataflowIds, connections);
  if (leftOut.length === 0) return null;
  const names = new Map(connections.map((row) => [row.id, row.displayName]));
  const nameOf = (id: string) => names.get(id);
  const why = leftOutSentence(leftOut, nameOf, "before");
  if (runnableIds.length === 0) {
    return {
      blocked: true,
      title: `No connection of ${schedule.name} can run`,
      message: `${why} ${leftOutNextStep(leftOut, nameOf)}`,
    };
  }
  return {
    blocked: false,
    title: leftOut.length === 1 ? "One connection will not run" : "Some connections will not run",
    message: `${why} ${runnableSentence(runnableIds, nameOf)}`,
  };
}

/** Round 6, S5: the target opened from its own record, as text. */
function FixedTarget({ kind, name }: { kind: RunTargetKind; name: string }) {
  return (
    <div className="flex flex-col gap-1" data-testid="run-now-target-fixed">
      <SkyText size={200} color="muted" block>
        {kind === "schedule" ? "Schedule" : "Connection"}
      </SkyText>
      <SkyText size={300} weight="semibold" block>
        {name}
      </SkyText>
    </div>
  );
}

function KindChoice({
  kind,
  noConnection,
  noSchedule,
  onChange,
}: {
  kind: RunTargetKind;
  noConnection: boolean;
  noSchedule: boolean;
  onChange: (kind: RunTargetKind) => void;
}) {
  return (
    <SkyRadioGroup
      label="What should run"
      layout="vertical"
      value={kind}
      onChange={(value) => onChange(value as RunTargetKind)}
      options={[
        {
          value: "dataflow",
          label: "One connection",
          description:
            "Imports that connection's selected entities and loads them. Nothing else runs.",
          disabled: noConnection,
        },
        {
          value: "schedule",
          label: "A whole schedule",
          description: `Runs every connection on the schedule. ${SCHEDULE_RUN_RULE}`,
          disabled: noSchedule,
        },
      ]}
      testId="run-now-kind"
    />
  );
}

function TableChoice({
  value,
  incremental,
  onChange,
}: {
  value: "all" | "choose";
  /** #1583: the import reads only changes: an incremental connection with Full refresh unticked. */
  incremental: boolean;
  onChange: (value: "all" | "choose") => void;
}) {
  return (
    <SkyRadioGroup
      label="Tables"
      layout="vertical"
      value={value}
      onChange={(next) => onChange(next as "all" | "choose")}
      options={[
        {
          value: "all",
          label: "All selected tables",
          description: incremental ? ALL_TABLES_INCREMENTAL : ALL_TABLES_FULL,
        },
        {
          value: "choose",
          label: "Choose tables",
          description:
            "Imports only the tables you pick. The others keep what they have until their next import.",
        },
      ]}
      testId="run-now-tables"
    />
  );
}

/** The Full refresh box, and the hint that a first incremental import reads everything anyway. */
function FullRefreshOption({
  choosing,
  checked,
  disabled,
  hint,
  onChange,
}: {
  choosing: boolean;
  checked: boolean;
  /** #984: the first-import sentence only before a first import, else what a plain run reads. */
  hint: string;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <>
      <SingleCheckbox
        label="Full refresh"
        description={
          choosing
            ? "Deletes and reloads every row of the chosen tables. Other tables are not touched. Takes longer."
            : FULL_REFRESH_HELP
        }
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        testId="run-now-full-refresh"
      />
      {/* Round 4 item 7a: an incremental import with nothing landed reads everything anyway. */}
      <SkyText size={200} color="muted" block testId="run-now-first-import-hint">
        {hint}
      </SkyText>
    </>
  );
}

/** The note under "Connections to import in full": #984 imported, #1238 whole days, #2521 moved. */
function untickedNote(input: { allImported: boolean; moved: boolean; wholeDays: boolean }): string {
  if (input.moved) {
    return input.wholeDays
      ? UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_MOVE
      : UNTICKED_CONNECTIONS_NOTE_AFTER_MOVE;
  }
  if (input.wholeDays) {
    return input.allImported
      ? UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS_AFTER_IMPORT
      : UNTICKED_CONNECTIONS_NOTE_WHOLE_DAYS;
  }
  return input.allImported ? UNTICKED_CONNECTIONS_NOTE_AFTER_IMPORT : UNTICKED_CONNECTIONS_NOTE;
}

/** Item 7: which of a schedule's incremental connections import in full. */
function PerConnectionFullRefresh({
  options,
  value,
  allImported,
  moved,
  wholeDays,
  onChange,
}: {
  /** #1238: a listed connection reads whole days, so "only what changed" would be false. */
  wholeDays: boolean;
  options: Array<{ id: string; label: string }>;
  /** #984: every listed connection has imported, so the note drops its first-import clause. */
  allImported: boolean;
  /** #2521: a listed connection's destination moved, so it reads everything again either way. */
  moved: boolean;
  value: string[];
  onChange: (ticked: Set<string>) => void;
}) {
  return (
    <div className="flex flex-col gap-1" data-testid="run-now-full-refresh-connections">
      <SkyCheckbox
        label="Connections to import in full"
        layout="vertical"
        options={options}
        value={value}
        onChange={(states) => {
          if (!Array.isArray(states)) return;
          onChange(new Set(states.filter((row) => row.checked).map((row) => row.id)));
        }}
      />
      <SkyText size={200} color="muted" block testId="run-now-unticked-note">
        {untickedNote({ allImported, moved, wholeDays })}
      </SkyText>
    </div>
  );
}

/**
 * Item 7: the incremental connections to import in full; "Full refresh" is all of them. Seeded
 * from the schedule's own choice, and derived again once when the connections first arrive.
 */
function useFullRefreshChoice({
  kind,
  targetId,
  connections,
  schedules,
  connectors,
  initialTarget,
}: {
  kind: RunTargetKind;
  targetId: string;
  connections: readonly DataflowSummary[];
  schedules: readonly ScheduleSummary[];
  connectors: ConnectorCatalog;
  initialTarget: { kind: RunTargetKind; id: string } | undefined;
}) {
  // A full-only connection the catalog had not listed yet is dropped by `forcedHere` below, and
  // Full refresh is sent only while it is offered (`startRunBody`), so a tick made before the
  // catalog arrived never travels (runNowDialog.fullOnlyConnector.test.tsx, "catalog late").
  const initialForced = (targetKind: RunTargetKind, id: string): string[] =>
    targetKind === "schedule" ? initialFullRefreshSet(id, connections, schedules, connectors) : [];
  const [forcedIds, setForcedIds] = useState<string[]>(() =>
    initialForced(initialTarget?.kind ?? kind, initialTarget?.id ?? ""),
  );
  // The connections query can resolve after the dialog mounted (opened from the schedules
  // page): derive the schedule's ticks again once, when they first arrive. Before that no box is
  // shown, so nothing the person ticked is overwritten.
  const [sawConnections, setSawConnections] = useState(connections.length > 0);
  if (!sawConnections && connections.length > 0) {
    setSawConnections(true);
    setForcedIds(initialForced(kind, targetId));
  }
  const offerFullRefresh = targetIsIncremental(kind, targetId, connections, schedules, connectors);
  const incrementalIds = incrementalConnectionsOf(
    kind,
    targetId,
    connections,
    schedules,
    connectors,
  );
  const setFullRefresh = (checked: boolean): void => setForcedIds(checked ? incrementalIds : []);
  const forcedSet = new Set(forcedIds);
  const forcedHere = incrementalIds.filter((id) => forcedSet.has(id));
  const fullRefresh = incrementalIds.length > 0 && forcedHere.length === incrementalIds.length;
  const perConnection = kind === "schedule" && incrementalIds.length > 1;
  return {
    initialForced,
    setForcedIds,
    offerFullRefresh,
    incrementalIds,
    setFullRefresh,
    forcedHere,
    fullRefresh,
    perConnection,
  };
}

/** Product gaps row 2: some tables of one connection. */
function useTableChoice(kind: RunTargetKind, targetId: string) {
  const tableRunsOn = useTableRunsEnabled();
  const [tableChoice, setTableChoice] = useState<"all" | "choose">("all");
  const [chosenTables, setChosenTables] = useState<string[]>([]);
  const [since, setSince] = useState("");
  // Searched on the server: the first page is not every table of a large source.
  const tableSearch = useServerTableSearch();
  const setTableSearch = tableSearch.setSearch;
  const offerTables = tableRunsOn && kind === "dataflow" && Boolean(targetId);
  const choosing = offerTables && tableChoice === "choose";
  const entities = useDataflowEntities(choosing ? targetId : null, {
    search: tableSearch.settled,
    selected: "selected",
    entityType: "all",
    page: 1,
    pageSize: TABLE_PICKER_PAGE,
  });
  const resetTables = (): void => {
    setTableChoice("all");
    setChosenTables([]);
    setSince("");
    setTableSearch("");
  };
  return {
    tableChoice,
    setTableChoice,
    chosenTables,
    setChosenTables,
    since,
    setSince,
    offerTables,
    choosing,
    entities,
    tableSearch,
    setTableSearch,
    resetTables,
  };
}

/** The schedule or connection to run. */
function TargetPicker({
  kind,
  options,
  targetId,
  onSelect,
}: {
  kind: RunTargetKind;
  options: Array<{ value: string; text: string }>;
  targetId: string;
  onSelect: (targetId: string) => void;
}) {
  return (
    <SkyDropdown
      label={kind === "schedule" ? "Schedule" : "Connection"}
      required
      placeholder={targetPlaceholder(kind, options.length)}
      options={options}
      selectedOptions={targetId ? [targetId] : []}
      onOptionSelect={(values) => onSelect(values[0] ?? "")}
      disabled={options.length === 0}
      listboxMaxHeight="16rem"
      testId="run-now-target"
    />
  );
}

/**
 * Full refresh with its hint, the first-import note, the schedule's per-connection choice and the
 * since date, each where it applies.
 */
function RefreshOptions({
  offerFullRefresh,
  choosing,
  fullRefresh,
  since,
  firstNote,
  importHint,
  wholeDays,
  fullOnly,
  everyRow,
  perConnection,
  incrementalIds,
  connectionName,
  forcedHere,
  onFullRefreshChange,
  onPerConnectionChange,
  onSinceChange,
}: {
  offerFullRefresh: boolean;
  choosing: boolean;
  fullRefresh: boolean;
  since: string;
  firstNote: string | null;
  importHint: string;
  /** #1238: some listed connection reads whole days. */
  wholeDays: boolean;
  /** #1077: one connection whose connector reads every row each time: said in place of Full refresh. */
  fullOnly: boolean;
  /** #1077: a schedule's connections that import every row each time, named (`everyRowSentence`). */
  everyRow: string | null;
  /** A schedule with more than one incremental connection: choose which import in full. */
  perConnection: boolean;
  incrementalIds: string[];
  connectionName: (id: string) => string;
  forcedHere: string[];
  onFullRefreshChange: (checked: boolean) => void;
  onPerConnectionChange: (ticked: Set<string>) => void;
  onSinceChange: (since: string) => void;
}) {
  const zone = useDisplayTz();
  return (
    <>
      {offerFullRefresh ? (
        <FullRefreshOption
          choosing={choosing}
          checked={fullRefresh}
          disabled={choosing && Boolean(since)}
          hint={importHint}
          onChange={onFullRefreshChange}
        />
      ) : null}
      {fullOnly ? (
        <SkyText size={200} color="muted" block testId="run-now-full-only-note">
          {FULL_ONLY_IMPORT_NOTE}
        </SkyText>
      ) : null}
      {everyRow ? (
        <SkyText size={200} color="muted" block testId="run-now-every-row">
          {everyRow}
        </SkyText>
      ) : null}
      {firstNote ? (
        // Round 6, S3: the schedule row's note, for the one connection this run imports.
        <SkyText size={200} color="muted" block testId="run-now-first-import-note">
          {firstNote}
        </SkyText>
      ) : null}

      {perConnection ? (
        <PerConnectionFullRefresh
          options={incrementalIds.map((id) => ({ id, label: connectionName(id) }))}
          value={forcedHere}
          allImported={importHint !== FIRST_IMPORT_HINT && !isMovedImportHint(importHint)}
          moved={isMovedImportHint(importHint)}
          wholeDays={wholeDays}
          onChange={onPerConnectionChange}
        />
      ) : null}

      {/* Review r2, N10: a full-refresh connection reads every table whole, so a since date is
        offered only where Full refresh is (an incremental connection). */}
      {choosing && offerFullRefresh ? (
        <SkyInput
          type="date"
          label="Read changes since (optional)"
          value={since}
          max={localDateIso(new Date(), zone)}
          min="1900-01-01"
          hint={sinceHelp(zone)}
          onChange={(event) => onSinceChange(event.target.value)}
          testId="run-now-since"
        />
      ) : null}
    </>
  );
}

export function RunNowDialog({
  open,
  onOpenChange,
  connections,
  schedules,
  initialTarget,
  allowKindChoice = true,
  targetFixed = false,
}: RunNowDialogProps) {
  const [, navigate] = useLocation();
  const notice = useNotice();
  // Session 10 (ux-s10 §3): a refusal stays inside the open dialog, and only there.
  const inline = useInlineError();
  const startRun = useStartRun();
  // #1078: a since date starts at midnight in the reader's display zone, which the start sends.
  const zone = useDisplayTz();
  // #1077: the connector decides whether an import can read only changes (`incrementalCapable`).
  const connectorList = useConnectors(open);
  const connectors = useMemo<ConnectorCatalog>(
    () => new Map((connectorList.data ?? []).map((connector) => [connector.name, connector])),
    [connectorList.data],
  );

  // #1008: opened from a schedule, "One connection" lists only that schedule's connections
  // (none until the schedules have loaded). Opened from anywhere else, every Ready connection.
  const scheduleScope = useMemo(
    () =>
      initialTarget?.kind === "schedule"
        ? new Set(schedules.find((row) => row.id === initialTarget.id)?.dataflowIds ?? [])
        : null,
    [initialTarget, schedules],
  );
  const readyConnections = useMemo(
    () =>
      connections.filter(
        (connection) =>
          connection.state === "ready" &&
          (scheduleScope === null || scheduleScope.has(connection.id)),
      ),
    [connections, scheduleScope],
  );

  // Seeded once, because the parent mounts this dialog only while it is open.
  // That is what stops the same component instance carrying a target from the
  // previous open and running the wrong connection.
  const [kind, setKind] = useState<RunTargetKind>(
    initialTarget?.kind ?? (readyConnections.length > 0 ? "dataflow" : "schedule"),
  );
  const [targetId, setTargetId] = useState<string>(initialTarget?.id ?? "");
  const {
    initialForced,
    setForcedIds,
    offerFullRefresh,
    incrementalIds,
    setFullRefresh,
    forcedHere,
    fullRefresh,
    perConnection,
  } = useFullRefreshChoice({
    kind,
    targetId,
    connections,
    schedules,
    connectors,
    initialTarget,
  });
  const connectionName = (id: string): string => {
    const connection = connections.find((row) => row.id === id);
    return connection ? connection.displayName : id;
  };
  const {
    tableChoice,
    setTableChoice,
    chosenTables,
    setChosenTables,
    since,
    setSince,
    offerTables,
    choosing,
    entities,
    tableSearch,
    setTableSearch,
    resetTables,
  } = useTableChoice(kind, targetId);
  // One Idempotency-Key per open (the parent mounts this dialog only while it is open): a second
  // click, or a retry after a response that never arrived, answers the run the first one started.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  // `startRun.isPending` reaches the button on the NEXT render, so two clicks inside that window
  // would both call `mutateAsync`. A ref makes one click one request now.
  const inFlight = useRef(false);

  const options = targetOptions(kind, schedules, readyConnections);

  // S3: only for one incremental connection importing incrementally (a full read needs no note).
  const firstNote =
    offerFullRefresh && !fullRefresh
      ? firstImportNoteFor(kind, targetId, connections, connectors)
      : null;

  // #1077: a schedule's visible connections that import every row each time, named, so neither
  // the hint nor the per-connection note reads as saying they import only changes. #1673: only
  // the ones this run imports.
  const everyRowNames =
    kind === "schedule"
      ? runnableMembersOf(
          schedules.find((row) => row.id === targetId),
          connections,
        ).flatMap((id) => {
          const connection = connections.find((row) => row.id === id);
          return connection && !incrementalIds.includes(id) ? [connection.displayName] : [];
        })
      : [];

  // #984: the connections this run could import incrementally, to say whether any import is first.
  const hinted =
    kind === "dataflow"
      ? connections.filter((row) => row.id === targetId)
      : connections.filter((row) => incrementalIds.includes(row.id));
  const plainHint = importHintFor(hinted);
  // #1238: a connector that re-reads whole days never "reads only changes".
  const wholeDaysCount = hinted.filter((row) => readsWholeDays(row, connectors)).length;
  const wholeDays = wholeDaysCount > 0;
  let importHint = plainHint;
  if (plainHint === AFTER_FIRST_IMPORT_HINT) {
    if (wholeDaysCount === hinted.length) importHint = AFTER_FIRST_IMPORT_HINT_WHOLE_DAYS;
    else if (wholeDays) importHint = AFTER_FIRST_IMPORT_HINT_MIXED;
    else if (everyRowNames.length > 0) importHint = AFTER_FIRST_IMPORT_HINT_SOME;
  }
  // #2521: an imported connection that reads whole days never reads "only changes".
  if (
    plainHint === MOVED_MIXED_IMPORT_HINT &&
    hinted.some((row) => hasImported(row) && readsWholeDays(row, connectors))
  ) {
    importHint = MOVED_MIXED_IMPORT_HINT_WHOLE_DAYS;
  }

  // #1077: one connection on a connector that reads every row each time says so.
  const fullOnly =
    kind === "dataflow" &&
    connectorIsFullOnly(
      connections.find((row) => row.id === targetId),
      connectors,
    );

  const fixed = targetFixed && initialTarget !== undefined;
  // #1673: what a whole schedule's run leaves out, said before the click.
  const leftOutNotice =
    kind === "schedule" && targetId
      ? scheduleLeftOutNotice(
          schedules.find((row) => row.id === targetId),
          connections,
        )
      : null;
  const canStart =
    Boolean(targetId) &&
    !startRun.isPending &&
    (!choosing || chosenTables.length > 0) &&
    leftOutNotice?.blocked !== true;

  const targetName = (targetKind: RunTargetKind, id: string): string => {
    if (targetKind === "schedule") {
      return schedules.find((row) => row.id === id)?.name ?? "the selected schedule";
    }
    const connection = connections.find((row) => row.id === id);
    return connection ? connection.displayName : "the selected connection";
  };

  const start = async (): Promise<void> => {
    if (!targetId || inFlight.current) return;
    inFlight.current = true;
    inline.clear();
    try {
      const result = await startRun.mutateAsync({
        body: startRunBody({
          kind,
          targetId,
          offerFullRefresh,
          fullRefresh,
          forcedHere,
          tables: choosing ? { since, entityNames: chosenTables, zone } : null,
        }),
        idempotencyKey,
      });
      const runPage = `/runs/${result.runId}`;
      // #1673: the server's own list of what it left out, named.
      const leftOut = leftOutSentence(
        result.leftOut ?? [],
        (id) => connections.find((row) => row.id === id)?.displayName,
      );
      onOpenChange(false);
      // Posted after the close (outside the modal's hidden window) and for the run page, so it
      // survives the navigation there and is read under that page's title (ux-s10 §3). It names
      // what was imported, never the run id (a live line would read a whole id aloud), and has
      // no "View import": the page it lands on is that import.
      const queued = `Import queued for ${targetName(kind, targetId)}.`;
      notice.success(leftOut ? `${queued} ${leftOut}` : queued, { forPath: runPage });
      navigate(absolutePath(runPage));
    } catch (error) {
      // Inside the dialog, which stays open: a 422 here names the connection that is not
      // ready, and that is the sentence the user has to act on.
      inline.show(error, "The run could not be started.");
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Run now"
      description="A run starts as soon as the runner picks it up. It does not change the schedule."
      size="medium"
      testId="run-now-dialog"
      // SkyDialog closes itself once a primary action resolves, which removed the inline error
      // below before anyone could read it. The close on success is explicit (`onOpenChange(false)`
      // in `start`), so the automatic one is declined.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: startRun.isPending ? "Starting…" : "Start the run",
        onClick: start,
        disabled: !canStart,
        loading: startRun.isPending,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {fixed ? <FixedTarget kind={kind} name={targetName(kind, targetId)} /> : null}
        {allowKindChoice && !targetFixed ? (
          <KindChoice
            kind={kind}
            noConnection={readyConnections.length === 0}
            noSchedule={schedules.length === 0}
            onChange={(value) => {
              setKind(value);
              setTargetId("");
              // A box ticked for one target never carries over to the next.
              setFullRefresh(false);
              resetTables();
            }}
          />
        ) : null}

        {fixed ? null : (
          <TargetPicker
            kind={kind}
            options={options}
            targetId={targetId}
            onSelect={(next) => {
              setTargetId(next);
              // A box ticked for one target never carries over; a schedule starts as it runs itself.
              setForcedIds(initialForced(kind, next));
              resetTables();
            }}
          />
        )}

        {scheduleScope !== null && kind === "dataflow" ? (
          <SkyText size={200} color="muted" block testId="run-now-not-counted">
            {NOT_COUNTED_IN_SCHEDULE}
          </SkyText>
        ) : null}

        {offerTables ? (
          <TableChoice
            value={tableChoice}
            incremental={offerFullRefresh && !fullRefresh}
            onChange={(value) => {
              setTableChoice(value);
              setChosenTables([]);
              setSince("");
              setTableSearch("");
            }}
          />
        ) : null}

        {choosing ? (
          <TablePicker
            rows={
              searchPending(tableSearch, entities)
                ? []
                : (entities.data?.items ?? []).map((entity) => ({ name: entity.name }))
            }
            selected={chosenTables}
            onChange={setChosenTables}
            ariaLabel="Tables to import"
            loading={entities.isLoading}
            searchPending={searchPending(tableSearch, entities)}
            // While a search is answered, the last answer's count is not this search's.
            note={
              searchPending(tableSearch, entities)
                ? null
                : cutListNote(
                    TABLE_PICKER_PAGE,
                    entities.data?.total ?? 0,
                    Boolean(tableSearch.settled),
                  )
            }
            error={entities.isError ? TABLE_LIST_ERROR : null}
            onSearchChange={setTableSearch}
            testId="run-now-table-picker"
          />
        ) : null}

        <RefreshOptions
          offerFullRefresh={offerFullRefresh}
          choosing={choosing}
          fullRefresh={fullRefresh}
          since={since}
          firstNote={firstNote}
          importHint={importHint}
          wholeDays={wholeDays}
          fullOnly={fullOnly}
          everyRow={everyRowSentence(everyRowNames)}
          perConnection={perConnection}
          incrementalIds={incrementalIds}
          connectionName={connectionName}
          forcedHere={forcedHere}
          onFullRefreshChange={(checked) => {
            setFullRefresh(checked);
            if (checked) setSince("");
          }}
          onPerConnectionChange={(ticked) =>
            setForcedIds(incrementalIds.filter((id) => ticked.has(id)))
          }
          onSinceChange={(value) => {
            setSince(value);
            if (value) setFullRefresh(false);
          }}
        />

        {leftOutNotice ? (
          <SkyMessageBar
            intent={leftOutNotice.blocked ? "error" : "warning"}
            layout="multiline"
            title={leftOutNotice.title}
            message={leftOutNotice.message}
            testId="run-now-left-out"
          />
        ) : null}

        {kind === "dataflow" && readyConnections.length === 0 ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="No connection can run yet"
            message="Validate a connection and select at least one entity, then start a run from here."
          />
        ) : null}

        <InlineErrorBar
          error={inline.error}
          title={inline.error?.retryable ? undefined : "The run could not be started"}
          testId="run-now-error"
        />
      </div>
    </SkyDialog>
  );
}
